import React from 'react';

import { appliedRuleIcon } from '../../../assets/images';
import { formatter } from '../../../utility';

const StepFour = (props) => {
  const { stepOneData, stepThreeData } = props;

  const selectedRentIncreaseAmount = stepThreeData.selectedRentIncreaseAmount;
  const phaseOneLostEligibleAmount = stepThreeData.phaseOneLostEligibleAmount;
  const phaseOneRemainingEligibleAmount = stepThreeData.phaseOneRemainingEligibleAmount;
  const minRentIncreaseDate = stepOneData.minRentIncreaseDate;
  const maxRentIncreaseDate = stepOneData.maxRentIncreaseDate;
  const rentAmountAdditionalPortion = stepThreeData.rentAmountAdditionalPortion;
  const calcRentIncreaseServiceTimeIsAvailable = stepOneData.calcRentIncreaseServiceTimeIsAvailable;
  const calcMissedRentIncreaseDate = stepOneData.calcMissedRentIncreaseDate;
  const calcRentIncreaseCausedLoss = stepOneData.calcRentIncreaseCausedLoss;
  const not1YearBetweenRentIncreases = stepOneData.not1YearBetweenRentIncreases;
  const not3MonthsForService = stepOneData.not3MonthsForService;

  const renderUnusedAmountWarning = () => {
    if (phaseOneLostEligibleAmount <= 0 || !selectedRentIncreaseAmount) return;
    return (
      <div className="alert-error w1-step-four__warning">
        <b>Warning.</b> The amount that you have selected for the additional rent increase does not use the full amount available. Based on the amount entered of <b>{formatter.toAmountDisplay(selectedRentIncreaseAmount)}</b> you 
        will lose <b>{formatter.toAmountDisplay(phaseOneLostEligibleAmount)}</b> of
        your Phase 1 eligible amount as an unused portion. This unused portion cannot be carried forward to the next phase or any future rent increase.
      </div>
    )
  };

  const renderNotOneYearBetweenRentIncreaseError = () => {
    if (!not1YearBetweenRentIncreases) return;
    return (
      <p className="w1-step-two__alert alert-error">
        <b>Warning: </b>The landlord may only increase the rent 12 months after the date that the existing rent was established or when rent was last increased. Based on the information you provided, for your previous and future rent increase, 
        your future rent increase does not comply with the requirements under the Residential Tenancy Act and you should not continue with this tool until this issue is resolved. 
        If the dates you have entered are incorrect you can enter the correct dates and, if the future rent increase is valid, this warning will disappear.
      </p>
    );
  }

  const renderNotThreeMonthsForServiceError = () => {
    if (!not3MonthsForService) return;
    return (
      <p className="w1-step-two__alert alert-error">
        <b>Warning: </b>All rent increases must have the notice of rent increase served to the tenant at least 3 full months prior to the date that the rent increase is to be effective. 
        Based on the information you provided, the notice that you served for your future rent increase does not comply with the requirements under the Residential Tenancy Act and you 
        should not continue with this tool until this issue is resolved. If the dates you have entered are incorrect, you can enter the correct dates and if the future rent increase is 
        valid this warning will disappear.
      </p>
    );
  }

  const renderMissedRentIncreaseWarning = () => {
    if (calcRentIncreaseServiceTimeIsAvailable) return

    return (
      <div className="alert-error w1-step-four__warning">
        <b>Warning.</b> Based on the information that you provided, the final month for the Phase 1 additional rent increase to take effect is {formatter.toDateDisplay(calcMissedRentIncreaseDate)}.  
        As there is less than three full months to provide notice to the tenant as required under the Act, your eligible amount for the Phase 1 additional rent increase has become an unused portion in the amount of <b>{formatter.toAmountDisplay(rentAmountAdditionalPortion)}</b> This
         unused portion cannot be carried forward to the next phase or any future rent increase. Your remaining eligible amount is <b>{formatter.toAmountDisplay(phaseOneRemainingEligibleAmount)}</b> for a Phase 2 additional rent increase. You must generate a notice insert for this lost Phase 1 eligible amount and include it with your future additional rent increases.
      </div>
    );
  }

  const renderAdditionalMissedRentIncreaseWarning = () => {
    if (!calcRentIncreaseCausedLoss) return;

    return (
      <div className="alert-error w1-step-four__warning">
        <b>Warning.</b> Based on the rules, your annual allowable rent increase effective on {formatter.toDateDisplay(calcMissedRentIncreaseDate)} will restrict you from imposing the Phase 1 additional rent increase during this same time frame and will result in the loss of the Phase 1 eligible amount of <b>{formatter.toAmountDisplay(rentAmountAdditionalPortion)}</b>.
      </div>
    );
  }

  const renderNoRemainingEligibleAmountWarning = () => {
    if (phaseOneRemainingEligibleAmount >= 1 || (not1YearBetweenRentIncreases || not3MonthsForService)) return;

    return (
      <div className="alert-error w1-step-four__warning">
        <b>Warning.</b> Based on the rules and the information provided, you have $0.00 eligible amount remaining for future additional rent increases. The rent for this rental unit may be increased with the annual allowable rent increase, subject to the timing and notice requirements under the Act.
      </div>
    );
  }

  const renderRegularWarnings = () => {
    return (
      <>
        { renderUnusedAmountWarning() }

        <div className="w1-step-four__rules">
          <p className="w1-step-four__rule">
            <img className="w1-step-four__rule__icon" src={appliedRuleIcon} alt='' />
            <span>
              <b>Applied Rule</b>: Remaining eligible amounts associated to a rental unit that exceed 3% of the rent, after the maximum annual allowable increase is added, can be applied to future rent increases.
              <span className="w1-step-four__rule--success">&nbsp;The remaining eligible amount for future additional rent increases is <b>{formatter.toAmountDisplay(phaseOneRemainingEligibleAmount)}</b> </span>
            </span>
          </p>

          <p className="w1-step-four__rule">
            <img className="w1-step-four__rule__icon" src={appliedRuleIcon} alt='' />
            <span>
              <b>Applied Rule</b>: Any additional rent increase amount eligible for Phase 1 that is not used within the first 12 months it may be imposed (to comply with timing and notice requirements) becomes an unused portion. 
              This unused portion cannot be carried forward to the next phase or any future rent increase. 
              <span className="w1-step-four__rule--error"> If no additional rent increase is applied between {formatter.toDateDisplay(minRentIncreaseDate)} and {formatter.toDateDisplay(maxRentIncreaseDate)} the eligible amount that will be lost and becomes an unused portion is <b>{formatter.toAmountDisplay(rentAmountAdditionalPortion)}</b>, 
              which will leave a remaining eligible amount for this unit of <b>{formatter.toAmountDisplay(phaseOneRemainingEligibleAmount)}</b> for future additional rent increases.</span>
            </span>
          </p>
        </div>

        { renderMissedRentIncreaseWarning() }
        { renderAdditionalMissedRentIncreaseWarning() }
        { renderNoRemainingEligibleAmountWarning() }
      </>
    )
  }

  const renderSpecialWarnings = () => {
    return <>
      { renderNotOneYearBetweenRentIncreaseError() }
      { renderNotThreeMonthsForServiceError() }
    </>
  }

  return (
    <div className="w1-step-four">
      {not1YearBetweenRentIncreases || not3MonthsForService ? renderSpecialWarnings() : renderRegularWarnings()}
    </div>
  );
}

export default StepFour;